import { useParams, Navigate } from "react-router-dom";
import { getSeoPage, getRelatedSeoPages, getStepsForPage } from "@/lib/seo-content";
import { getCategoryBySlug, CATEGORIES } from "@/lib/seo-categories";
import { resolveAssetPath } from "@/lib/seo-image-loader";
import SEOHead from "@/components/seo/SEOHead";
import SeoBreadcrumbs, { buildBreadcrumbJsonLd } from "@/components/seo/SeoBreadcrumbs";
import SeoHero from "@/components/seo/SeoHero";
import TrustBar from "@/components/seo/TrustBar";
import PricingTable from "@/components/seo/PricingTable";
import KalyanLocalSignals from "@/components/seo/KalyanLocalSignals";
import FaqAccordion, { buildFaqJsonLd } from "@/components/seo/FaqAccordion";
import RelatedServices from "@/components/seo/RelatedServices";
import CTASection from "@/components/seo/CTASection";
import DoctorBlock from "@/components/seo/DoctorBlock";
import StepsJourney from "@/components/seo/StepsJourney";
import BeforeAfterCarousel from "@/components/BeforeAfterCarousel";
import { getServicePhotoCases } from "@/lib/before-after-data";
import ServiceTestimonials from "@/components/seo/ServiceTestimonials";
import ServiceBlogs from "@/components/seo/ServiceBlogs";
import InlineBookingForm from "@/components/seo/InlineBookingForm";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const SITE = "https://muthaadvanceddentalcare.in";

interface SeoServicePageProps {
  slugOverride?: string;
  canonicalOverride?: string;
  breadcrumbsOverride?: { label: string; path?: string }[];
}

const SeoServicePage = ({ slugOverride, canonicalOverride, breadcrumbsOverride }: SeoServicePageProps = {}) => {
  const params = useParams<{ slug: string }>();
  const slug = slugOverride ?? params.slug ?? "";
  const page = getSeoPage(slug);
  if (!page) return <Navigate to="/services" replace />;

  const category = getCategoryBySlug(page.slug);
  if (!category) return <Navigate to="/services" replace />;
  const treatmentPhotos = getServicePhotoCases(page.slug);
  const heroSrc = resolveAssetPath(page.heroImage);
  const journey = getStepsForPage(page.slug);
  const related = getRelatedSeoPages(page.related).map((r) => ({
    slug: r.slug,
    name: r.h1.split(" — ")[0].split(" in Kalyan")[0],
    blurb: r.subheadline,
  }));

  const shortName = page.h1.split(" — ")[0].split(" in Kalyan")[0];
  const ctaLabel = `Book ${page.eyebrow} Consultation`;
  // Map the page to the closest booking dropdown option
  const slugLower = page.slug.toLowerCase();
  const defaultOption =
    /root-canal|tooth-pain|rct/.test(slugLower) ? "Tooth Pain / Root Canal" :
    /implant|all-on|full-mouth/.test(slugLower) ? "Dental Implants / Missing Teeth" :
    /aligner|invisalign|braces|orthodont/.test(slugLower) ? "Braces / Invisalign / Aligners" :
    /veneer|smile-design|cosmetic|makeover/.test(slugLower) ? "Smile Makeover / Veneers" :
    /whitening/.test(slugLower) ? "Teeth Whitening" :
    /kid|child|pediatric/.test(slugLower) ? "Child Dentistry" :
    /cleaning|scaling|check-up|checkup|hygiene/.test(slugLower) ? "Teeth Cleaning / Check-up" :
    /emergency/.test(slugLower) ? "Emergency Dental Care" :
    "";

  const canonical = canonicalOverride ?? `/services/${page.slug}`;
  const crumbs = breadcrumbsOverride ?? [
    { label: "Services", path: "/services" },
    { label: category.shortName, path: `/services/${category.hubSlug}` },
    { label: page.h1.split(" — ")[0].split(" in Kalyan")[0] },
  ];

  const jsonLd: object[] = [
    buildBreadcrumbJsonLd(crumbs, SITE, canonical),
    buildFaqJsonLd(page.faqs),
    {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: page.procedureName || page.h1,
      description: page.metaDescription,
      bodyLocation: "Mouth",
      procedureType: "https://schema.org/TherapeuticProcedure",
      url: SITE + canonical,
    },
  ];

  return (
    <article>
      <SEOHead
        title={page.title}
        description={page.metaDescription}
        canonical={canonical}
        ogImage={SITE + heroSrc}
        jsonLd={jsonLd}
      />
      <SeoHero
        eyebrow={page.eyebrow}
        h1={page.h1}
        subheadline={page.subheadline}
        image={heroSrc}
        imageAlt={page.heroAlt}
        whatsappMessage={`Hi, I'd like to know more about ${page.eyebrow} at Mutha's Advanced Dental Care Kalyan.`}
        ctaLabel={ctaLabel}
      />
      <div className="container py-4">
        <SeoBreadcrumbs items={crumbs} />
      </div>
      <TrustBar />

      {/* Intro */}
      <section className="py-12 md:py-16">
        <div className="container max-w-3xl">
          {page.intro.split("\n\n").map((para, i) => (
            <p key={i} className="text-base md:text-lg text-foreground/85 leading-relaxed mb-5">
              {para}
            </p>
          ))}
        </div>
      </section>

      {/* Body sections */}
      {page.sections.length > 0 && (
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container max-w-4xl space-y-12">
            {page.sections.map((s, i) => (
              <div key={i}>
                <h2 className="font-heading text-2xl md:text-3xl font-light mb-4">{s.heading}</h2>
                <p className="text-muted-foreground leading-relaxed mb-5">{s.body}</p>
                {s.bullets && (
                  <ul className="space-y-2.5">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 items-start">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2.5 shrink-0" />
                        <span className="text-foreground/85 leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {page.pricing && <PricingTable rows={page.pricing.rows} title={page.pricing.title} />}

      <StepsJourney
        eyebrow={journey.eyebrow}
        title={journey.title}
        steps={journey.steps}
      />

      {/* Mid-page CTA — right after the treatment process */}
      <section className="py-10 bg-primary/5 border-y border-border">
        <div className="container max-w-3xl text-center">
          <h2 className="font-heading text-xl md:text-2xl font-light mb-4">
            Ready to start your {page.eyebrow.toLowerCase()} treatment in Kalyan?
          </h2>
          <Button asChild size="lg" className="w-full sm:w-auto h-auto min-h-12 py-3 px-4 whitespace-normal tracking-normal">
          <a
            href="#book"
          >
            {ctaLabel}
          </a>
          </Button>
        </div>
      </section>

      {(page.showBeforeAfter || treatmentPhotos) && (
        <BeforeAfterCarousel
          cases={treatmentPhotos}
          category={page.beforeAfterCategory && page.beforeAfterCategory !== "All" ? page.beforeAfterCategory : undefined}
          eyebrow="REAL PATIENT RESULTS"
          heading="Before & After at Mutha's Advanced Dental Care Kalyan"
        />
      )}

      <ServiceTestimonials slug={page.slug} eyebrow={page.eyebrow} />
      <ServiceBlogs slug={page.slug} eyebrow={page.eyebrow} />
      <DoctorBlock />
      <FaqAccordion faqs={page.faqs} />
      <InlineBookingForm
        treatment={shortName}
        defaultOption={defaultOption}
        ctaLabel={ctaLabel}
        sourcePage={`service_${page.slug}`}
      />
      <KalyanLocalSignals />
      <RelatedServices items={related} />
      <CTASection
        ctaLabel={ctaLabel}
        ctaHref="#book"
        whatsappMessage={`Hi, I'd like to book a consultation for ${page.eyebrow} at Mutha's Advanced Dental Care Kalyan.`}
      />
    </article>
  );
};

export default SeoServicePage;